/* global RequestInit */
import { isArray, some } from 'lodash';

import { getSearchPattern } from './format.helpers';

export type Extractor<TId extends number | string, TData extends Object[], TItem = unknown> = {
  (item: TData[number], index: number, data: TData): { id: TId; item: TItem } | undefined | null;
};

export type NormalizedDataKeys = 'ids' | 'indices' | 'pool' | 'data';

type Ids<TId extends number | string> = TId[];
type Indices = Record<number | string, number>;
type Pool<TId extends number | string, TItem> = Record<TId, TItem>;
type Data<TItem> = TItem[];

export type NormalizedData<TItem, DataKeys extends NormalizedDataKeys, TId extends number | string> = {
  [Key in DataKeys]: Key extends 'ids'
    ? Ids<TId>
    : Key extends 'indices'
      ? Indices
      : Key extends 'pool'
        ? Pool<TId, TItem>
        : Key extends 'data'
          ? Data<TItem>
          : never;
};

type HasData = {
  ids: Ids<any>;
  indices: Indices;
  pool: Pool<any, any>;
  data: Data<any>;
};

// ----------------------------------------------------------------------

const has = <Key extends keyof HasData>(
  _normalizedData: any,
  includes: string[],
  key: Key,
): _normalizedData is Record<Key, HasData[Key]> => includes.includes(key);

// ----------------------------------------------------------------------

export const normalizeData = <
  TId extends number | string,
  TData extends Object[],
  TItem extends TData[number],
  TIncludes extends NormalizedDataKeys[] = Array<'pool' | 'ids'>,
>(
  raw: TData,
  extractor: Extractor<TId, TData, TItem> = (item: any) => ({ id: item.id, item }),
  includes: TIncludes = ['pool', 'ids'] as TIncludes,
) => {
  const normalizedData = {} as NormalizedData<TItem, TIncludes[number], TId>;

  const hasIds = has(normalizedData, includes, 'ids');
  const hasIndices = has(normalizedData, includes, 'indices');
  const hasPool = has(normalizedData, includes, 'pool');
  const hasData = has(normalizedData, includes, 'data');

  if (hasIds) normalizedData.ids = [];
  if (hasIndices) normalizedData.indices = {};
  if (hasPool) normalizedData.pool = {};
  if (hasData) normalizedData.data = [];

  raw.forEach((rawItem, idx) => {
    const extract = extractor(rawItem, idx, raw);

    if (!extract) return;
    if (hasIds) normalizedData.ids.push(extract.id);
    if (hasIndices) normalizedData.indices[extract.id] = idx;
    if (hasPool) normalizedData.pool[extract.id] = extract.item;
    if (hasData) normalizedData.data.push(extract.item);
  });

  return normalizedData;
};

export const urlToBlob = async (url: string, fetchParams?: RequestInit) => {
  const data = await fetch(url, fetchParams);
  const blob = await data.blob();

  return blob;
};

export const urlToBase64 = async (url: string) => {
  const blob = await urlToBlob(url);

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result.split('base64,')[1]);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = () => reject(reader.error);
  });
};

type UrlToFile = {
  url: string;
  fetchParams?: RequestInit;
  fileName?: string;
};

export const urlToFile = async (params: UrlToFile) => {
  const { url, fetchParams, fileName = 'file' } = params;

  const blob = await urlToBlob(url, fetchParams);
  const file = new File([blob], fileName, { type: blob.type });

  return file;
};

// ----------------------------------------------------------------------

type FilterValue = string | null | undefined;

export type FilterBySearchQuerySettings<TSearchedList extends Array<Record<string, unknown>>> = {
  searchQuery: string;
  list: TSearchedList;
  extractListItemFilterValue: (option: TSearchedList[number]) => FilterValue | FilterValue[];
};

export const filterBySearchQuery = <TSearchedList extends Array<Record<string, unknown>>>(
  settings: FilterBySearchQuerySettings<TSearchedList>,
) => {
  const { searchQuery, list, extractListItemFilterValue } = settings;

  if (!searchQuery) {
    return list;
  }

  const searchPattern = getSearchPattern(searchQuery);

  return list.filter(listItem => {
    const valueOrArray = extractListItemFilterValue(listItem);

    if (isArray(valueOrArray)) {
      return some(valueOrArray, value => !!value && searchPattern.test(value));
    }

    return !!valueOrArray && searchPattern.test(valueOrArray);
  }) as TSearchedList;
};
